import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  TimeTablesPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
} from "./TimeTablesPage.styles";
import Sidebar from "components/Sidebar";
import Table from "components/Table";
import IconButton from "components/Buttons/IconButton";
import { ModuleTimeTable } from "services/documentService/documentService.types";
import {
  fetchModuleTimeTables,
  createModuleTimeTable,
  updateModuleTimeTable,
  deleteModuleTimeTable,
} from "services/documentService/documentService";
import Button from "components/Buttons/Button";
import { ButtonWrapper } from "../../TemplatesPage.styles";
import { ConfirmModal, CreateModal } from "components/Modals";
import DeletedRow from "components/DeletedRow";
import RestoredRow from "components/RestoredRow";
import BreadcrumbLink from "components/BreadcrumbLink/BreadcrumbLink";
import { ButtonsContainer } from "pages/DocumentVersionsPage/DocumentVersionsPage.styles";
import { formatServerErrors } from "utils/errorHandler";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData } from "utils/httpError";
import { delIcon, editIcon } from "assets/icons";
const TimeTablesPage: React.FC = () => {
  const navigate = useNavigate();
  const { moduleId, documentId } = useParams<{ moduleId: string, documentId: string }>();
  logger.log(moduleId, documentId);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [timetableToEdit, setTimetableToEdit] = useState<ModuleTimeTable | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const toast = useNotify();

  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("templateId") || "0";

  const {
    items: timetables,
    setItems: setTimetables,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadTimetables,
  } = useListPage<ModuleTimeTable, Awaited<ReturnType<typeof fetchModuleTimeTables>>>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchModuleTimeTables(Number(moduleId), page, size),
    select: (response) => ({ items: response.results, total: response.count }),
    errorMessage: "Ошибка загрузки расписаний",
  });
  const documentName = searchParams.get("documentName") || "Неизвестный документ";
  const templateName = searchParams.get("templateName") || "Неизвестный шаблон";
  const moduleName = searchParams.get("moduleName") || "Неизвестный модуль";

  // Состояния для подтверждения удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [timetableToDelete, setTimetableToDelete] = useState<ModuleTimeTable | null>(null);

  // Состояния для отложенного удаления и уведомлений
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!moduleId) {
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Необходимо указать модуль.",
      });
      navigate("/login");
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);



  const openDeleteModal = (tt: ModuleTimeTable) => {
    setTimetableToDelete(tt);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTimetableToDelete(null);
  };

  const confirmDelete = () => {
    if (timetableToDelete) {
      handleDelete(timetableToDelete.id);
      closeDeleteModal();
    }
  };

  const handleDelete = (id: number) => {
    setPendingDelete(id);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteModuleTimeTable(id);
        setPendingDelete(null);
        toast.show({
          severity: "success",
          summary: "Удалено",
          detail: "Расписание успешно удалено",
          life: 2000,
        });
        loadTimetables();
      } catch (error) {
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось удалить расписание",
          life: 3000,
        });
        logger.error("Ошибка при удалении расписания:", error);
        setPendingDelete(null);
      }
    }, 5000);
  };

  const restoreHandler = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    const restoredId = pendingDelete;
    setPendingDelete(null);
    setRecentlyRestored(restoredId);
    toast.show({
      severity: "success",
      summary: "Восстановлено",
      detail: "Удаление расписания отменено",
      life: 3000,
    });
    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  const openCreateModal = () => {
    setTimetableToEdit(null);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (tt: ModuleTimeTable) => {
    setTimetableToEdit(tt);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateOrEdit = async (formData: Record<string, string | File[] | null>) => {
    setExternalErrors({});
    try {
      const moduleIdNum = Number(moduleId);
      if (isNaN(moduleIdNum)) {
        throw new Error("Неверный ID модуля");
      }
      if (timetableToEdit) {
        await updateModuleTimeTable(timetableToEdit.id, {
          week_num: Number(formData["week_num"]),
          hours: Number(formData["hours"]),
          module: moduleIdNum,
        });
        setRecentlyEdited(timetableToEdit.id);
        toast.show({
          severity: "success",
          summary: "Успех",
          detail: "Расписание обновлено",
          life: 3000,
        });
      } else {
        const created = await createModuleTimeTable(moduleIdNum, {
          week_num: Number(formData["week_num"]),
          hours: Number(formData["hours"]),
          module: moduleIdNum,
        });
        setRecentlyCreated(created.id);
        toast.show({
          severity: "success",
          summary: "Успех",
          detail: "Расписание создано",
          life: 3000,
        });
      }
      closeCreateModal();
      loadTimetables();
      setTimeout(() => {
        setRecentlyCreated(null);
        setRecentlyEdited(null);
      }, 3000);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
        
        // Показываем toast только для общих ошибок (non_field_errors)
        if (formattedErrors.non_field_errors) {
          toast.show({
            severity: "error",
            summary: "Ошибка",
            detail: formattedErrors.non_field_errors,
            life: 3000,
          });
        }
      } else {
        logger.error("Ошибка при сохранении расписания:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка сохранения расписания",
          life: 3000,
        });
      }
    }
  };

  const headers = [
    { label: "Номер недели", key: "week_num", fieldName: "week_num" },
    { label: "Часы", key: "hours", fieldName: "hours" },
    { label: "", key: "buttons", sortable: false, fieldName: "delete" },
  ];

  const rows = timetables.map((tt) => {
    if (tt.id === pendingDelete) {
      return {
        week_num: (
          <DeletedRow
            id={tt.id}
            onRestore={restoreHandler}
            colSpan={headers.length}
            message={`Расписание для недели ${tt.week_num} будет удалено через 5 секунд.`}
            targetText="Отменить удаление"
          />
        ),
      };
    }
    return {
      week_num: tt.week_num,
      hours: tt.hours,
      buttons: (
        <ButtonsContainer>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openEditModal(tt)}
          />
          <IconButton
            icon={delIcon}
            tone="danger"
            altText="Удалить"
            onClick={() => openDeleteModal(tt)}
          />
        </ButtonsContainer>
      ),
    };
  });

  // Вставляем строки уведомлений, как в CoursesPage
  if (recentlyRestored !== null) {
    const restoredIndex = timetables.findIndex((t) => t.id === recentlyRestored);
    if (restoredIndex !== -1) {
      rows.splice(restoredIndex + 1, 0, {
        week_num: (
          <RestoredRow colSpan={headers.length} message="Расписание успешно восстановлено" />
        ),
      });
    }
  }
  if (recentlyCreated !== null) {
    const createdIndex = timetables.findIndex((t) => t.id === recentlyCreated);
    if (createdIndex !== -1) {
      rows.splice(createdIndex + 1, 0, {
        week_num: (
          <RestoredRow colSpan={headers.length} message="Расписание успешно создано" />
        ),
      });
    }
  }
  if (recentlyEdited !== null) {
    const editedIndex = timetables.findIndex((t) => t.id === recentlyEdited);
    if (editedIndex !== -1) {
      rows.splice(editedIndex + 1, 0, {
        week_num: (
          <RestoredRow colSpan={headers.length} message="Расписание успешно отредактировано" />
        ),
      });
    }
  }

  const rowAmountOptions = [
    { label: "7 записей", value: 7 },
    { label: "15 записей", value: 15 },
    { label: "20 записей", value: 20 },
  ];

  return (
    <TimeTablesPageContainer>
      <Sidebar />
      <ContentWrapper>
        {/* Хлебные крошки */}
        <div style={{ marginBottom: "16px" }}>
          <BreadcrumbLink href="/documents" text="Шаблоны документов" />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/documents/${templateId}/versions?templateName=${templateName}`}
            text={`Шаблон ${templateName}`}
          />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/versions/${documentId}/modules?documentName=${documentName}&templateId=${templateId}&templateName=${templateName}`}
            text={`Модули документа ${documentName}`}
          />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink href={`/modules/${documentId}/${moduleId}/timetables?moduleName=${moduleName}&documentName=${documentName}&templateId=${templateId}&templateName=${templateName}`} text={`Расписание модуля ${moduleName}`} />
        </div>
        <HeaderContainer>
          <Title>Расписание модуля</Title>
          <ButtonWrapper>
            <Button onClick={openCreateModal} variant="primary" size="lg">
              Добавить расписание
            </Button>
          </ButtonWrapper>
        </HeaderContainer>
        <Table
          emptyState={{
            title: "Расписаний пока нет",
            description:
              "Добавьте расписание, чтобы закрепить занятия за днями.",
          }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
          minHeight="200px"
          onSort={handleSort}
          specialRowKey="week_num"
        />
      </ContentWrapper>
      {isCreateModalOpen && (
        <CreateModal
          title={timetableToEdit ? "Редактировать расписание" : "Новое расписание"}
          fields={[
            { name: "week_num", type: "number", placeholder: "Номер недели", required: true },
            { name: "hours", type: "number", placeholder: "Часы", required: true },
          ]}
          onSubmit={handleCreateOrEdit}
          submitButtonText={timetableToEdit ? "Сохранить" : "Создать"}
          onCancel={closeCreateModal}
          initialValues={
            timetableToEdit
              ? {
                  week_num: timetableToEdit.week_num.toString(),
                  hours: timetableToEdit.hours.toString(),
                }
              : {}
          }
          externalErrors={externalErrors}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить расписание для недели ${timetableToDelete?.week_num}?`}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          confirmButtonText="Удалить"
        />
      )}
    </TimeTablesPageContainer>
  );
};

export default TimeTablesPage;
